import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { DisplaySizes } from '@newcore/ui/enums/typography-display-sizes';
import { Display, Text } from '@newcore/ui/typography';
import { captureException } from '@sentry/react';
import { FactorType } from '@/api/generated';
import { FactorRouter } from '@/components/factors/factor-router';
import { Page } from '@/components/ui/page';
import { IframeMessageType } from '@/enums/iframe-message-types';
import type { FactorStep } from '@/hooks/use-mfa-orchestrator.types';
import { makeFullPath } from '@/utils/path-utils';
import { navigateToSplitSigning, submitSsoCompletion } from '@/utils/sso-utils';

export default function AuthMfaFactorPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const factorTypeParam = searchParams.get('factorType');
  const authId = searchParams.get('authId');
  const isEscalation = searchParams.get('escalation') === 'true';
  const isEnrollment = searchParams.get('enrollment') === 'true';

  // Get initial step data and hasAlternative flag from navigation state
  const navigationState = location.state as {
    currentStepData?: FactorStep;
    hasAlternative?: boolean;
    username?: string;
  } | null;
  const initialStepData = navigationState?.currentStepData;
  const initialHasAlternative = navigationState?.hasAlternative;
  const username = navigationState?.username;

  const onBack = useCallback(() => {
    const authIdParam = authId ? `?auth_id=${encodeURIComponent(authId)}` : '';
    navigate(`${makeFullPath('/login')}${authIdParam}`, {
      state: username ? { presetUsername: username } : undefined,
    });
  }, [navigate, authId, username]);

  const factorType: FactorType | null = useMemo(() => {
    if (!factorTypeParam) {
      return null;
    }

    const normalizedType = factorTypeParam.toLowerCase();

    // Check if the normalized type is a valid FactorType enum value
    return (
      Object.values(FactorType).find(value => value === normalizedType) ?? null
    );
  }, [factorTypeParam]);

  const navigateWithSaml = async () => {
    console.info('MFA factor completed, redirecting to app');

    try {
      const completion = await submitSsoCompletion();

      if (completion) {
        navigateToSplitSigning(navigate, completion, username);
      }
    } catch (error) {
      console.error('Failed to submit SAML form:', error);
      captureException(error);
    }
  };

  const handleComplete = async () => {
    // Detect iframe context - bulletproof check
    const isInIframe = (() => {
      try {
        return window.self !== window.top;
      } catch (_e) {
        // Cross-origin iframe throws error - not our case (same origin)
        return false;
      }
    })();

    // Build the parent origin for postMessage targetOrigin.
    // The iframe is at <tenant>.spark.dev (subdomain routing),
    // while the parent (user-portal) is at app.spark.dev (path routing).
    // Derive by replacing the tenant subdomain with "app".
    const getParentOrigin = (): string | null => {
      // host, not hostname: postMessage matches the whole origin, port included.
      const { protocol, host } = window.location;
      const dotIndex = host.indexOf('.');

      if (dotIndex === -1) {
        console.error('getParentOrigin: host has no subdomain:', host);

        return null;
      }

      const baseDomain = host.substring(dotIndex + 1);

      return `${protocol}//app.${baseDomain}`;
    };

    // Handle enrollment completion via postMessage
    if (isEnrollment && isInIframe && authId) {
      const parentOrigin = getParentOrigin();

      if (!parentOrigin) {
        console.error('Cannot send enrollment postMessage: no parent origin');

        return;
      }

      try {
        window.parent.postMessage(
          {
            type: IframeMessageType.ENROLLMENT_COMPLETED,
            authId: authId,
            timestamp: Date.now(),
          },
          parentOrigin
        );
        console.info('Enrollment completion message sent to parent');

        return; // Don't proceed with SAML redirect
      } catch (error) {
        // Enrollment flows only support postMessage - no SAML fallback
        console.error('Failed to send enrollment postMessage.', error);
        captureException(error);

        return; // Don't proceed with SAML redirect
      }
    }

    // Handle escalation completion via postMessage
    if (isEscalation && isInIframe && authId) {
      const parentOrigin = getParentOrigin();

      if (!parentOrigin) {
        console.error('Cannot send escalation postMessage: no parent origin');

        return;
      }

      try {
        window.parent.postMessage(
          {
            type: IframeMessageType.ESCALATION_COMPLETED,
            authId: authId,
            timestamp: Date.now(),
          },
          parentOrigin
        );
        console.info('Escalation completion message sent to parent');

        return; // Don't proceed with SAML redirect
      } catch (error) {
        console.error('Failed to send escalation postMessage.', error);

        captureException(error);

        return;
      }
    }

    // Normal flow (not in iframe)
    await navigateWithSaml();
  };

  if (!factorType) {
    return (
      <Page center>
        <div className='text-center space-y-6'>
          <Display
            as='h1'
            size={DisplaySizes.XS}
            weight={FontWeights.BOLD}
            className='text-error-primary'
          >
            Invalid Request
          </Display>
          <Text as='p' className='text-secondary'>
            {!factorTypeParam
              ? 'Missing required factorType parameter'
              : `Invalid factor type: ${factorTypeParam}. Supported types: push_notification, password`}
          </Text>
        </div>
      </Page>
    );
  }

  return (
    <FactorRouter
      factorType={factorType}
      initialStepData={initialStepData}
      initialHasAlternative={initialHasAlternative}
      onComplete={handleComplete}
      username={username}
      onBack={username ? onBack : undefined}
      isEscalationFlow={isEscalation}
    />
  );
}
